import { StandardEvents } from '@shopify/events';

/**
 * Opt-in checkbox that adds a free ring sizing kit to the cart as a follow-up
 * request whenever the main product's add-to-cart succeeds and the box is checked.
 * Deliberately does NOT hook into product-form-component's own submit/FormData —
 * a separate /cart/add.js call after the fact can't interfere with the main
 * item's variant/quantity-rule validation.
 */
class MudrikaSizingKitOptIn extends HTMLElement {
  connectedCallback() {
    this.checkbox = document.getElementById(this.dataset.checkboxId ?? '');
    this.kitVariantId = this.dataset.kitVariantId ?? null;
    this.productId = this.dataset.productId ?? null;
    this.adding = false;

    this._onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener(StandardEvents.cartLinesUpdate, this._onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this._onCartUpdate);
  }

  /** @param {CustomEvent & { promise?: Promise<{ detail?: any }> }} event */
  onCartUpdate(event) {
    if (!this.checkbox?.checked || !this.kitVariantId) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail || detail.didError) return;
        if (detail.source !== 'product-form-component') return;
        if (this.productId && detail.productId && detail.productId !== this.productId) return;

        this.addKitToCart();
      })
      .catch(() => {});
  }

  async addKitToCart() {
    if (this.adding) return;
    this.adding = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.kitVariantId, quantity: 1 }] }),
      });

      if (!response.ok) throw new Error(`Failed to add sizing kit: ${response.status}`);

      await this.refreshCart();
      if (this.checkbox) this.checkbox.checked = false;
    } catch (error) {
      console.warn('[mudrika-sizing-kit] Failed to add free sizing kit:', error);
    } finally {
      this.adding = false;
    }
  }

  async refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItemsComponent).fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
  }
}

if (!customElements.get('mudrika-sizing-kit-optin')) {
  customElements.define('mudrika-sizing-kit-optin', MudrikaSizingKitOptIn);
}
